import { useState, useMemo, useCallback, useRef, useEffect } from "react";

/* ═══════════════════════════════════════════════════
   MODULE IMPORTS
   ═══════════════════════════════════════════════════ */
// Engine & data (Phase 1)
import * as calc from './engine';
import { SWR_DEFAULT } from './constants/financial';
import { DEF } from './constants/defaults';

// Theme & config (Phase 2)
import { theme } from './theme/tokens';
import { tooltipStyle } from './theme/tooltipStyle';
import { TABS } from './constants/tabs';

// Icons — only the ones used by the App shell (sidebar, mobile header)
import { Moon, Sun, MenuIcon, XIcon, ChevronRight } from './components/icons';

// UI atoms used by App shell
import { Toast } from './components/Toast';
import { ConfirmModal } from './components/ConfirmModal';

// Context (Phase 4A)
import { AppContext } from './context/AppContext';

// Page components (Phase 3)
import Overview from './pages/Overview';
import Career from './pages/Career';
import Budget from './pages/Budget';
import Investments from './pages/Investments';
import RealEstate from './pages/RealEstate';
import Pension from './pages/Pension';
import Projections from './pages/Projections';
import Milestones from './pages/Milestones';
import SettingsTab from './pages/SettingsTab';

// Onboarding (Phase 5A)
import OnboardingWizard from './features/onboarding/OnboardingWizard';

/* ═══════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════ */
export default function App() {
  const [s, setS] = useState(DEF);
  const [tab, setTab] = useState("overview");
  const [mob, setMob] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [mcData, setMcData] = useState(null);
  const [mcRunning, setMcRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  const showToast = useCallback((msg, type = "info") => setToast({ msg, type }), []);

  /* ─── Persistence via window.storage ─── */
  useEffect(() => {
    (async () => {
      try {
        if (window.storage) {
          const res = await window.storage.get("wm-state-v2");
          if (res?.value) {
            const parsed = JSON.parse(res.value);
            setS({ ...DEF, ...parsed });
            showToast("Restored from last session", "success");
          }
        }
      } catch (e) { /* first load or no storage */ }
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(async () => {
      try {
        if (window.storage) {
          await window.storage.set("wm-state-v2", JSON.stringify(s));
        }
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [s, loaded]);

  const up = useCallback((k, v) => setS((p) => ({ ...p, [k]: v })), []);
  const upM = useCallback((o) => setS((p) => ({ ...p, ...o })), []);
  const c = useMemo(() => theme(s.dark), [s.dark]);
  const proj = useMemo(() => calc.run(s), [s]);
  const scen = useMemo(() => tab === "projections" ? calc.scen(s) : null, [s, tab]);
  const amor = useMemo(() => (tab === "realestate" || tab === "overview") ? calc.amor(s) : null, [s, tab]);
  const p0 = useMemo(() => proj[0] || {}, [proj]);
  const pF = useMemo(() => proj[proj.length - 1] || {}, [proj]);
  const fiAge = useMemo(() => proj.find((d) => d.fi >= d.liv)?.a || (s.maxAge || 40), [proj, s.maxAge]);
  const fiReached = useMemo(() => proj.some((d) => d.fi >= d.liv), [proj]);

  const blendedReturn = useMemo(() => {
    const eq = (s.xeqt != null ? s.xeqt : 100) / 100;
    return eq * s.ret + (1 - eq) * 0.04;
  }, [s.ret, s.xeqt]);

  const realReturn = useMemo(() => blendedReturn - (s.inflation || 0.025), [blendedReturn, s.inflation]);

  const coastAge = useMemo(() =>
    calc.coastFI(proj, s.maxAge || 40, SWR_DEFAULT, blendedReturn),
  [proj, s.maxAge, blendedReturn]);

  // Budget-derived living expense for display
  const budgetMonthly = useMemo(() =>
    (s.cats || DEF.cats).reduce((a, x) => a + x.a, 0),
  [s.cats]);

  const switchTab = useCallback((id) => {
    setTab(id); setMob(false); setAnimKey((k) => k + 1);
  }, []);

  const tt = useMemo(() => tooltipStyle(c), [c]);

  const runMonteCarlo = useCallback(() => {
    setMcRunning(true);
    setTimeout(() => {
      const data = calc.monte(s, 500);
      setMcData(data);
      setMcRunning(false);
      showToast("500 simulations complete", "success");
    }, 50);
  }, [s, showToast]);


  /* ═══════════ CONTEXT VALUE ═══════════ */
  const appValue = useMemo(() => ({
    s, c, proj, pF, p0, up, upM, setS, tt,
    fiAge, fiReached, coastAge, budgetMonthly,
    blendedReturn, realReturn, amor, scen,
    mcData, mcRunning, runMonteCarlo,
    showToast, setConfirm, fileRef,
  }), [
    s, c, proj, pF, p0, up, upM, setS, tt,
    fiAge, fiReached, coastAge, budgetMonthly,
    blendedReturn, realReturn, amor, scen,
    mcData, mcRunning, runMonteCarlo,
    showToast, setConfirm, fileRef,
  ]);

  /* ═══════════ TAB MAP ═══════════ */
  const TAB_MAP = {
    overview: Overview, career: Career, budget: Budget,
    investments: Investments, realestate: RealEstate, pension: Pension,
    projections: Projections, milestones: Milestones, settings: SettingsTab,
  };
  const ActiveTab = TAB_MAP[tab] || Overview;


  /* ═══════════ RENDER ═══════════ */
  return (
    <AppContext.Provider value={appValue}>
    <div style={{
      fontFamily: "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      background: c.bg, color: c.fg, minHeight: "100vh",
      display: "flex", transition: "background 0.4s, color 0.4s",
    }}>

      {/* ═══════ ONBOARDING GATE (Phase 5A) ═══════
          Show nothing until storage is read (loaded).
          Then show onboarding OR the main app. */}
      {!loaded ? null : !s.onboarded ? (
        <OnboardingWizard />
      ) : (
      <>
      {/* Mobile overlay */}
      {mob && (
        <div onClick={() => setMob(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          zIndex: 30, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        }} />
      )}

      {/* Sidebar */}
      <nav id="sidebar" style={{
        width: 230, flexShrink: 0, background: c.sidebar,
        borderRight: `1px solid ${c.border}`,
        padding: "28px 14px", display: "flex", flexDirection: "column",
        position: "fixed", top: 0, bottom: 0, zIndex: 40,
        transition: "transform 0.35s cubic-bezier(0.25,0.1,0.25,1), background 0.4s",
        transform: "translateX(0)", overflowY: "auto",
      }}>
        <div style={{ padding: "0 10px", marginBottom: 36 }}>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.03em", color: c.fg }}>
            Wealthmaxxing
          </div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: c.blue,
            letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 3,
          }}>
            Plan OS
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {TABS.map((ti) => {
            const Ic = ti.ic;
            const active = tab === ti.id;
            return (
              <button key={ti.id} onClick={() => switchTab(ti.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 12px", borderRadius: 10, border: "none",
                  cursor: "pointer", fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  background: active ? c.hover : "transparent",
                  color: active ? c.fg : c.muted,
                  transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
                  width: "100%", textAlign: "left",
                }}>
                <Ic size={15} strokeWidth={active ? 2.2 : 1.6} style={{ color: active ? c.blue : c.muted, transition: "color 0.2s" }} />
                {ti.l}
                {active && <ChevronRight size={12} style={{ marginLeft: "auto", color: c.muted }} />}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: "auto", padding: "20px 10px 0" }}>
          <div onClick={() => up("dark", !s.dark)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer", color: c.muted, fontSize: 12,
              padding: "8px 0", userSelect: "none", transition: "color 0.2s",
            }}>
            {s.dark ? <Sun size={13} /> : <Moon size={13} />}
            {s.dark ? "Light Mode" : "Dark Mode"}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main id="main-content" style={{
        flex: 1, marginLeft: 230,
        padding: "32px 44px 56px",
        maxWidth: 1100,
      }}>
        {/* Mobile header */}
        <div id="mobile-header" style={{
          display: "none", position: "fixed", top: 0, left: 0, right: 0,
          zIndex: 50, padding: "12px 20px",
          background: s.dark ? "rgba(0,0,0,0.8)" : "rgba(245,245,247,0.85)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${c.border}`,
          alignItems: "center", justifyContent: "space-between",
        }}>
          <button onClick={() => setMob(!mob)} style={{ background: "none", border: "none", color: c.fg, cursor: "pointer", padding: 4 }}>
            {mob ? <XIcon size={20} /> : <MenuIcon size={20} />}
          </button>
          <span style={{ fontSize: 14, fontWeight: 700, color: c.fg, letterSpacing: "-0.02em" }}>
            Wealthmaxxing Plan OS
          </span>
          <button onClick={() => up("dark", !s.dark)} style={{ background: "none", border: "none", color: c.fg, cursor: "pointer", padding: 4 }}>
            {s.dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        {/* Page title */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{
            fontSize: 30, fontWeight: 800,
            letterSpacing: "-0.04em", margin: 0, color: c.fg,
          }}>
            {TABS.find((x) => x.id === tab)?.l}
          </h1>
        </div>

        {/* Active tab content */}
        <div key={tab}>
          <ActiveTab />
        </div>
      </main>
      </>
      )}

      {/* Toast notifications */}
      {toast && <Toast msg={toast.msg} type={toast.type} c={c} onClose={() => setToast(null)} />}

      {/* Confirm modal */}
      {confirm && <ConfirmModal msg={confirm.msg} onOk={confirm.onOk} onCancel={() => setConfirm(null)} c={c} />}

      {/* Global styles */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
          #mobile-header { display: flex !important; }
          #sidebar { transform: translateX(${mob ? "0" : "-100%"}) !important; }
          #main-content { margin-left: 0 !important; padding: 72px 16px 36px !important; }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          #main-content { padding: 28px 24px 48px !important; }
        }

        * { box-sizing: border-box; margin: 0; }
        body { margin: 0; background: ${c.bg}; overflow-x: hidden; }

        input[type=range] {
          -webkit-appearance: none; appearance: none;
          height: 4px; border-radius: 2px;
          background: ${c.border}; outline: none; cursor: pointer;
        }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px; height: 20px; border-radius: 50%;
          background: ${c.blue}; cursor: pointer;
          border: 3px solid ${s.dark ? "#000" : "#fff"};
          box-shadow: 0 1px 6px rgba(0,0,0,0.3);
          margin-top: -8px;
          transition: transform 0.15s cubic-bezier(0.34,1.56,0.64,1);
        }
        input[type=range]::-webkit-slider-thumb:active { transform: scale(1.2); }
        input[type=range]::-webkit-slider-runnable-track {
          height: 4px; border-radius: 2px; background: ${c.border};
        }
        input[type=range]::-moz-range-thumb {
          width: 20px; height: 20px; border-radius: 50%;
          background: ${c.blue}; cursor: pointer;
          border: 3px solid ${s.dark ? "#000" : "#fff"};
          box-shadow: 0 1px 6px rgba(0,0,0,0.3);
        }
        input[type=number] { outline: none; }
        input[type=number]:focus {
          border-color: ${c.blue} !important;
          box-shadow: 0 0 0 3px rgba(10,132,255,0.15);
        }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${c.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${c.muted}; }

        button { transition: all 0.15s cubic-bezier(0.25,0.1,0.25,1); }
        button:active { transform: scale(0.97); }
        .wm-card:hover { transform: translateY(-1px); box-shadow: ${s.dark ? "0 4px 16px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)"}; }
        .wm-trow:hover { background: ${c.hover}; }
      `}</style>
    </div>
    </AppContext.Provider>
  );
}
